import { Capacitor } from '@capacitor/core'
// Static on purpose: utils/capture-photo.ts already imports the Camera plugin
// statically, so a dynamic import here just triggers Vite's mixed-import
// warning without moving the plugin out of the entry chunk.
import { Camera } from '@capacitor/camera'
import { isNativeApp } from '../../utils/platform'
import {
  getNativeNotificationPermission,
  requestNativeNotificationPermission,
} from '../../utils/request-notification-permission'
import {
  getWebNotificationPermission,
  requestWebNotificationPermission,
} from '../../utils/web-call-notification'
import {
  requestCameraPermission,
  requestMicrophonePermission,
} from '../../utils/request-call-permissions'
import { queryGeolocationPermission } from '../../features/sessions/location/geolocation-permission'
import {
  checkNativeMicrophonePermission,
  openNativeAppSettings,
  requestNativeMicrophonePermission,
} from '../voice-call/native-audio-route'
import { registerForWebPush } from '../web-push/web-push-registration'
import type { DeniedPermissionKind } from './permission-denied'

/**
 * Cross-platform permission service boundary for the guided onboarding flow.
 *
 * UI never talks to Capacitor plugins / the browser permission APIs directly —
 * it goes through this module, which:
 *  - knows WHICH permissions are applicable on the current platform (web has a
 *    graceful subset: notifications + mic + camera + location; native-only bits
 *    like PushKit VoIP are handled by the push layer, not here),
 *  - exposes a non-prompting `checkPermission` (for the checklist ticks) and a
 *    prompting `requestPermission` (fires the real OS dialog on a user gesture),
 *  - can deep-link to the OS app settings on native for re-enabling after a hard
 *    denial.
 *
 * Each request reuses the app's existing, battle-tested request helpers so the
 * actual native ↔ web behaviour (e.g. iOS mic via getUserMedia, Android
 * POST_NOTIFICATIONS via Capacitor, FCM token registration after a notification
 * grant) stays exactly as it is elsewhere.
 */

export type PermissionKind = 'notifications' | 'microphone' | 'camera' | 'location'

/**
 * Normalized status across platforms:
 *  - `granted`     – allowed, nothing more to do
 *  - `denied`      – blocked; a fresh request won't re-prompt (must use Settings)
 *  - `prompt`      – not decided yet; a request WILL show the OS dialog
 *  - `unsupported` – not applicable on this platform/build (skip it)
 */
export type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'unsupported'

/** Stable display order used by the onboarding checklist. */
const PERMISSION_ORDER: PermissionKind[] = ['notifications', 'microphone', 'camera', 'location']

/**
 * Permissions requested UP FRONT during the guided onboarding on the current
 * platform.
 *
 * Native (iOS/Android): all four are requested sequentially.
 *
 * Web (Chrome/Safari): notifications + mic + camera are offered up front, but
 * LOCATION is intentionally DEFERRED — browsers tie the geolocation prompt to
 * the secure origin and it reads as intrusive at first login, so we ask for it
 * contextually the first time the user actually shares their location (live
 * location share / a call). We still drop a permission entirely when the
 * platform genuinely cannot honour it (e.g. no Notification API in an old web
 * view).
 */
export function listApplicablePermissions(): PermissionKind[] {
  if (isNativeApp()) return [...PERMISSION_ORDER]

  return PERMISSION_ORDER.filter((kind) => {
    switch (kind) {
      case 'notifications':
        return typeof window !== 'undefined' && 'Notification' in window
      case 'microphone':
      case 'camera':
        return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia)
      case 'location':
        // Deferred to first contextual use on web (see doc comment above).
        return false
      default:
        return false
    }
  })
}

// --- Camera (native) -------------------------------------------------------

function mapCapacitorState(state: string | undefined): PermissionStatus {
  if (state === 'granted' || state === 'limited') return 'granted'
  if (state === 'denied') return 'denied'
  if (state === 'prompt' || state === 'prompt-with-rationale') return 'prompt'
  return 'prompt'
}

async function checkNativeCamera(): Promise<PermissionStatus> {
  try {
    const status = await Camera.checkPermissions()
    return mapCapacitorState(status.camera)
  } catch {
    return 'prompt'
  }
}

async function requestNativeCamera(): Promise<PermissionStatus> {
  try {
    const status = await Camera.requestPermissions({ permissions: ['camera'] })
    return mapCapacitorState(status.camera)
  } catch {
    // Plugin unavailable (old shell) — fall back to a getUserMedia request.
    return requestCameraPermission().then((s) => (s === 'unsupported' ? 'prompt' : s))
  }
}

// --- Location --------------------------------------------------------------

type GeolocationPluginStatus = { location?: string; coarseLocation?: string }
type GeolocationPlugin = {
  checkPermissions(): Promise<GeolocationPluginStatus>
  requestPermissions(options?: { permissions?: string[] }): Promise<GeolocationPluginStatus>
}

function mapNativeLocation(status: GeolocationPluginStatus | undefined): PermissionStatus {
  if (!status) return 'prompt'
  const best = [status.location, status.coarseLocation]
  if (best.some((s) => s === 'granted')) return 'granted'
  if (best.some((s) => s === 'prompt' || s === 'prompt-with-rationale')) return 'prompt'
  if (best.some((s) => s === 'denied')) return 'denied'
  return 'prompt'
}

async function checkNativeLocation(): Promise<PermissionStatus> {
  try {
    const { Geolocation } = (await import('@capacitor/geolocation')) as unknown as {
      Geolocation: GeolocationPlugin
    }
    return mapNativeLocation(await Geolocation.checkPermissions())
  } catch {
    return 'prompt'
  }
}

async function requestNativeLocation(): Promise<PermissionStatus> {
  try {
    const { Geolocation } = (await import('@capacitor/geolocation')) as unknown as {
      Geolocation: GeolocationPlugin
    }
    return mapNativeLocation(
      await Geolocation.requestPermissions({ permissions: ['location', 'coarseLocation'] }),
    )
  } catch {
    return 'prompt'
  }
}

/**
 * Android: Capacitor reports `location` granted only with FINE (precise);
 * `coarseLocation` alone means the person chose approximate.
 */
function nativeLocationPrecision(status: GeolocationPluginStatus | undefined): 'precise' | 'approximate' | 'unknown' {
  if (status?.location === 'granted') return 'precise'
  if (status?.coarseLocation === 'granted') return 'approximate'
  return 'unknown'
}

/** Android precise vs approximate location (non-prompting). iOS needs the native probe. */
export async function checkAndroidLocationPrecision(): Promise<'precise' | 'approximate' | 'unknown'> {
  if (!isNativeApp() || Capacitor.getPlatform() !== 'android') return 'unknown'
  try {
    const { Geolocation } = (await import('@capacitor/geolocation')) as unknown as {
      Geolocation: GeolocationPlugin
    }
    return nativeLocationPrecision(await Geolocation.checkPermissions())
  } catch {
    return 'unknown'
  }
}

/**
 * Android 12+: asking for FINE while only COARSE is granted shows the system
 * "change to precise" dialog (until the person has refused it twice).
 */
export async function requestAndroidPreciseLocation(): Promise<'precise' | 'approximate' | 'unknown'> {
  if (!isNativeApp() || Capacitor.getPlatform() !== 'android') return 'unknown'
  try {
    const { Geolocation } = (await import('@capacitor/geolocation')) as unknown as {
      Geolocation: GeolocationPlugin
    }
    return nativeLocationPrecision(await Geolocation.requestPermissions({ permissions: ['location'] }))
  } catch {
    return 'unknown'
  }
}

function requestWebLocation(): Promise<PermissionStatus> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve('unsupported')
  }
  return new Promise<PermissionStatus>((resolve) => {
    navigator.geolocation.getCurrentPosition(
      () => resolve('granted'),
      (err) => {
        if (err && err.code === err.PERMISSION_DENIED) {
          resolve('denied')
          return
        }
        // Position unavailable / timeout but the user wasn't blocked — re-read the
        // permission state so a "Allow" that just couldn't get a fix still ticks.
        void queryGeolocationPermission().then((state) => {
          resolve(state === 'granted' ? 'granted' : state === 'denied' ? 'denied' : 'prompt')
        })
      },
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 },
    )
  })
}

// --- Web mic/camera non-prompting probe ------------------------------------

async function queryWebMediaPermission(name: 'microphone' | 'camera'): Promise<PermissionStatus> {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return 'prompt'
  try {
    // `microphone` / `camera` aren't in the TS PermissionName union everywhere.
    const result = await navigator.permissions.query({
      name: name as PermissionName,
    })
    if (result.state === 'granted') return 'granted'
    if (result.state === 'denied') return 'denied'
    return 'prompt'
  } catch {
    return 'prompt'
  }
}

// --- Public API ------------------------------------------------------------

/** Non-prompting read of a permission's current status (drives checklist ticks). */
export async function checkPermission(kind: PermissionKind): Promise<PermissionStatus> {
  switch (kind) {
    case 'notifications':
      if (isNativeApp()) return getNativeNotificationPermission()
      return mapWebNotification(getWebNotificationPermission())
    case 'camera':
      if (isNativeApp()) return checkNativeCamera()
      return queryWebMediaPermission('camera')
    case 'microphone':
      // The WebView cannot read mic permission (getUserMedia PROMPTS), so the
      // native probe goes through the AudioRoute plugin (AVCaptureDevice /
      // checkSelfPermission). Hardcoding 'prompt' here made the walkthrough
      // show the mic as un-granted on every replay.
      if (isNativeApp()) return checkNativeMicrophonePermission()
      return queryWebMediaPermission('microphone')
    case 'location':
      if (isNativeApp()) return checkNativeLocation()
      return mapWebGeolocation(await queryGeolocationPermission())
    default:
      return 'unsupported'
  }
}

/** Fire the real OS permission dialog. MUST be called from a user gesture. */
export async function requestPermission(kind: PermissionKind): Promise<PermissionStatus> {
  switch (kind) {
    case 'notifications': {
      if (isNativeApp()) return getNativeNotificationPermission().then(requestOrReturnNative)
      // Hard deny: Notification.requestPermission is a no-op — don't pretend to ask.
      const already = mapWebNotification(getWebNotificationPermission())
      if (already === 'denied') return 'denied'
      const webStatus = mapWebNotification(await requestWebNotificationPermission())
      // A fresh web grant is the moment to subscribe for closed-tab Web Push
      // (the SW + VAPID key are already in place). Fire-and-forget.
      if (webStatus === 'granted') void registerForWebPush()
      return webStatus
    }
    case 'camera':
      if (isNativeApp()) return requestNativeCamera()
      {
        const already = await queryWebMediaPermission('camera')
        if (already === 'denied') return 'denied'
      }
      return mapMediaResult(await requestCameraPermission())
    case 'microphone': {
      if (isNativeApp()) {
        // Read the real OS state first: iOS never re-shows the dialog once
        // granted OR denied, and probing with getUserMedia has audio-session
        // side effects mid-call. Only an undetermined state should prompt.
        const current = await checkNativeMicrophonePermission()
        if (current === 'granted') return current
        if (Capacitor.getPlatform() === 'android') {
          // The WebView's getUserMedia prompt hides a permanent refusal: Android
          // answers "denied" with no dialog and nothing remembers it, so the
          // button looked dead. The app's own plugin asks, remembers, and
          // reports `denied` at once. Even a remembered `denied` asks again: it
          // answers instantly when the refusal is real, and heals a stale marker
          // (denied, allowed in Settings, switched off again). Older shells lack
          // the method: fall through.
          const viaPlugin = await requestNativeMicrophonePermission()
          if (viaPlugin) return viaPlugin
        }
        // iOS never re-shows the dialog after a denial: report it, don't probe.
        if (current === 'denied') return current
      } else {
        const already = await queryWebMediaPermission('microphone')
        if (already === 'denied') return 'denied'
      }
      return mapMediaResult(await requestMicrophonePermission())
    }
    case 'location':
      if (isNativeApp()) return requestNativeLocation()
      {
        const already = mapWebGeolocation(await queryGeolocationPermission())
        if (already === 'denied') return 'denied'
      }
      return requestWebLocation()
    default:
      return 'unsupported'
  }
}

async function requestOrReturnNative(current: string): Promise<PermissionStatus> {
  // On a hard denial the OS won't re-prompt — surface 'denied' so the UI offers
  // an "Open settings" path instead of a no-op request.
  if (current === 'denied') return 'denied'
  const result = await requestNativeNotificationPermission()
  return result === 'unsupported' ? 'unsupported' : result
}

function mapWebNotification(state: NotificationPermission | 'unsupported'): PermissionStatus {
  if (state === 'granted') return 'granted'
  if (state === 'denied') return 'denied'
  if (state === 'default') return 'prompt'
  return 'unsupported'
}

function mapWebGeolocation(
  state: 'granted' | 'denied' | 'prompt' | 'unsupported' | 'unknown',
): PermissionStatus {
  if (state === 'granted') return 'granted'
  if (state === 'denied') return 'denied'
  if (state === 'unsupported') return 'unsupported'
  // 'prompt' and 'unknown' both mean "a request may still show a dialog".
  return 'prompt'
}

function mapMediaResult(state: 'granted' | 'denied' | 'unsupported'): PermissionStatus {
  return state === 'unsupported' ? 'unsupported' : state
}

/**
 * Open the OS app-settings screen (native only) so the user can re-enable a
 * permission they previously hard-denied. Returns false on web (no equivalent —
 * the UI shows browser-specific instructions instead).
 */
export async function openPermissionSettings(): Promise<boolean> {
  if (!isNativeApp()) return false
  // Preferred: the in-app AudioRoute helper (iOS UIApplication.openSettingsURLString,
  // Android ACTION_APPLICATION_DETAILS_SETTINGS).
  if (await openNativeAppSettings()) return true
  // Older installed shells may lack the helper. iOS can still deep-link to this
  // app's Settings page with the `app-settings:` scheme: Capacitor's navigation
  // delegate hands non-app URLs to UIApplication.open. (@capacitor/app 7 has no
  // openUrl.) Navigation gives no result, so report what we can observe: when
  // Settings really opens the app is backgrounded (page hidden) almost at once.
  if (Capacitor.getPlatform() === 'ios') {
    return new Promise<boolean>((resolve) => {
      const done = (opened: boolean) => {
        document.removeEventListener('visibilitychange', onChange)
        window.removeEventListener('pagehide', onHide)
        window.clearTimeout(timer)
        resolve(opened)
      }
      const onChange = () => {
        if (document.visibilityState === 'hidden') done(true)
      }
      const onHide = () => done(true)
      const timer = window.setTimeout(() => done(false), 2000)
      document.addEventListener('visibilitychange', onChange)
      window.addEventListener('pagehide', onHide)
      try {
        window.location.assign('app-settings:')
      } catch {
        done(false)
      }
    })
  }
  return false
}

/** True when the OS exposes a settings deep-link we can send the user to. */
export function canOpenAppSettings(): boolean {
  return isNativeApp()
}

/** Current platform label for copy ("iOS" / "Android" / "browser"). */
export function permissionPlatformLabel(): 'iOS' | 'Android' | 'browser' {
  if (!isNativeApp()) return 'browser'
  return Capacitor.getPlatform() === 'ios' ? 'iOS' : 'Android'
}

/**
 * True when the permission has since been granted (e.g. the user flipped it in
 * Settings and came back). Photos need no permission at all via the system
 * picker, so a return from Settings always counts as resolved.
 */
export async function isPermissionNowGranted(kind: DeniedPermissionKind): Promise<boolean> {
  if (kind === 'photos' || kind === 'contacts') return true
  try {
    return (await checkPermission(kind)) === 'granted'
  } catch {
    return false
  }
}
